import { useEffect, useMemo, useState } from "react";
import Top from "./Top";
import Footer from "./Footer";
import { useNavigate } from "react-router-dom";
import SLNScanHeader from "./SLNScanHeader";

const API_ALL_LATEST =
  "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/OptionChain/GetAllLatestOptionChainHistory";

/* =========================
   ✅ Helpers (NO duplicates)
   ========================= */
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function fmt2(v) {
  const n = toNum(v);
  return n === null ? "—" : n.toFixed(2);
}

function fmtPct(v) {
  const n = toNum(v);
  if (n === null) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${n.toFixed(2)}%`;
}

function chgClass(n) {
  const v = toNum(n);
  if (v === null) return "text-muted";
  return v >= 0 ? "text-success" : "text-danger";
}

// ratio style (>=1 green else red)
function ratioClass(v) {
  const n = toNum(v);
  if (n === null) return "text-muted";
  return n >= 1 ? "text-success" : "text-danger";
}

// checker style (>=0 green else red)
function checkerClass(v) {
  const n = toNum(v);
  if (n === null) return "text-muted";
  return n >= 0 ? "text-success" : "text-danger";
}

function AllCompaniesStock() {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const navigate = useNavigate();

  const [showCheckersPct, setShowCheckersPct] = useState(false);

  const [sort, setSort] = useState({
    key: null,
    dir: "asc",
  });

  function onSort(key) {
    setSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      return { key, dir: "asc" };
    });
  }

  function SortIcon({ col, sort }) {
    if (sort.key !== col) return <i className="bi bi-arrow-down-up ms-1" />;
    return sort.dir === "asc" ? (
      <i className="bi bi-arrow-up ms-1" />
    ) : (
      <i className="bi bi-arrow-down ms-1" />
    );
  }

  async function loadAll() {
    try {
      setErr("");
      setLoading(true);
      setRows([]);

      const token = localStorage.getItem("SLNToken");
      if (!token) throw new Error("Token not found in localStorage.");

      const url = `${API_ALL_LATEST}?page=1&limit=200`;
      const res = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      });

      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.s === "error") {
        throw new Error(json?.message || `Request failed (${res.status})`);
      }

      setRows(Array.isArray(json?.rows) ? json.rows : []);
    } catch (e) {
      setErr(e?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = rows;

    if (q) {
      list = list.filter(
        (r) =>
          String(r.short || "")
            .toLowerCase()
            .includes(q) ||
          String(r.symbol || "")
            .toLowerCase()
            .includes(q),
      );
    }

    if (!sort.key) return list;

    return [...list].sort((a, b) => {
      const av = a?.[sort.key];
      const bv = b?.[sort.key];

      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;

      const an = toNum(av);
      const bn = toNum(bv);

      if (an !== null && bn !== null) {
        return sort.dir === "asc" ? an - bn : bn - an;
      }

      return sort.dir === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
  }, [rows, search, sort]);

  useEffect(() => {
    loadAll();
  }, []);

  return (
    <>
      <Top />
      <SLNScanHeader page={"allstocks"} />

      <section className="mb-7 mt-3">
        <div className="container">
          <div className="mb-2">
            <div className="card-body">
              <div className="col-lg-5 col-md-7 col-12 mb-1">
                <div className="d-flex flex-column flex-sm-row align-items-center gap-2 w-100 w-md-auto">
                  <div className="input-group py-2">
                    <span className="input-group-text bg-white rounded-start-3">
                      <i className="bi bi-search" />
                    </span>
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      type="text"
                      className="form-control rounded-end-3"
                      placeholder="Search Symbol (e.g., TCS)"
                    />
                  </div>
                </div>
              </div>

              {err ? (
                <div className="alert alert-danger mt-2">
                  <div className="fw-semibold">Error</div>
                  <div className="small">{err}</div>
                </div>
              ) : null}

              <div className="d-flex justify-content-end align-items-center mb-3 mt-lg-n7">
                <div
                  style={{
                    display: "inline-flex",
                    borderRadius: 8,
                    border: "1px solid #cbd5e0",
                    overflow: "hidden",
                  }}
                >
                  <button
                    onClick={() => setShowCheckersPct(false)}
                    style={{
                      padding: "7px 22px",
                      fontSize: 12,
                      fontWeight: 500,
                      border: "none",
                      borderRight: "1px solid #cbd5e0",
                      cursor: "pointer",
                      transition: "all 0.15s",
                      whiteSpace: "nowrap",
                      background: !showCheckersPct ? "#000000" : "transparent",
                      color: !showCheckersPct ? "#fff" : "#718096",
                    }}
                  >
                    Value
                  </button>
                  <button
                    onClick={() => setShowCheckersPct(true)}
                    style={{
                      padding: "7px 28px",
                      fontSize: 12,
                      fontWeight: 500,
                      border: "none",
                      cursor: "pointer",
                      transition: "all 0.15s",
                      whiteSpace: "nowrap",
                      background: showCheckersPct ? "#000000" : "transparent",
                      color: showCheckersPct ? "#fff" : "#718096",
                    }}
                  >
                    Percentage %
                  </button>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-hover align-middle mb-0 border border-1">
                  <thead className="table-thead-bg">
                    <tr className="small text-white lh-sm">
                      <th
                        className="text-white text-center cursor-pointer white-space-nowrap"
                        onClick={() => onSort("short")}
                      >
                        SYMBOL <SortIcon col="short" sort={sort} />
                      </th>

                      <th
                        className="text-white text-center cursor-pointer white-space-nowrap"
                        onClick={() => onSort("volPCR")}
                      >
                        VOLUME
                        <br />
                        PCR <SortIcon col="volPCR" sort={sort} />
                      </th>

                      <th
                        className="text-white text-center cursor-pointer white-space-nowrap"
                        onClick={() => onSort("optionPCR")}
                      >
                        OPTION
                        <br />
                        PCR <SortIcon col="optionPCR" sort={sort} />
                      </th>

                      <th
                        className="text-white text-center cursor-pointer white-space-nowrap"
                        onClick={() => onSort("volCPR")}
                      >
                        VOLUME
                        <br />
                        CPR <SortIcon col="volCPR" sort={sort} />
                      </th>

                      <th
                        className="text-white text-center cursor-pointer white-space-nowrap"
                        onClick={() => onSort("ltpChange")}
                      >
                        LTP
                        <br />
                        CHANGE <SortIcon col="ltpChange" sort={sort} />
                      </th>

                      <th
                        className="text-white text-center cursor-pointer white-space-nowrap"
                        onClick={() => onSort("changePct")}
                      >
                        CHANGE
                        <br />% <SortIcon col="changePct" sort={sort} />
                      </th>

                      <th
                        className="text-white text-center cursor-pointer white-space-nowrap"
                        onClick={() => onSort("ltp")}
                      >
                        LTP <SortIcon col="ltp" sort={sort} />
                      </th>

                      <th
                        className="text-white text-center cursor-pointer white-space-nowrap"
                        onClick={() => onSort("ceContract")}
                      >
                        CE
                        <br />
                        CONT % <SortIcon col="ceContract" sort={sort} />
                      </th>

                      <th
                        className="text-white text-center cursor-pointer white-space-nowrap"
                        onClick={() => onSort("peContract")}
                      >
                        PE
                        <br />
                        CONT % <SortIcon col="peContract" sort={sort} />
                      </th>

                      <th
                        className="text-white text-center cursor-pointer white-space-nowrap"
                        onClick={() => onSort("volCheckerPCR")}
                      >
                        PCR
                        <br />
                        CHK <SortIcon col="volCheckerPCR" sort={sort} />
                      </th>

                      <th
                        className="text-white text-center cursor-pointer white-space-nowrap"
                        onClick={() => onSort("volCheckerCPR")}
                      >
                        VOL
                        <br />
                        CHK CPR <SortIcon col="volCheckerCPR" sort={sort} />
                      </th>

                      <th
                        className="text-white text-center cursor-pointer white-space-nowrap"
                        onClick={() => onSort("pressureRatio")}
                      >
                        BID-ASK
                        <br />
                        PR <SortIcon col="pressureRatio" sort={sort} />
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td
                          colSpan={13}
                          className="py-5 text-center text-muted"
                        >
                          {loading ? "Loading Scanner…" : "No rows found."}
                        </td>
                      </tr>
                    ) : (
                      filtered.map((r) => {
                        const ce = toNum(r.ceContract);
                        const pe = toNum(r.peContract);

                        const pcrChkPct =
                          toNum(r.volCheckerPCR) === null
                            ? null
                            : toNum(r.volCheckerPCR) * 100;

                        const volChkCprPct =
                          toNum(r.volCheckerCPR) === null
                            ? null
                            : toNum(r.volCheckerCPR) * 100;

                        return (
                          <tr
                            key={r.symbol}
                            className="text-black text-center fs-6"
                          >
                            <td>
                              <div className="d-flex align-items-center justify-content-center gap-2">
                                <div
                                  onClick={() =>
                                    navigate(
                                      `/option-chain?symbol=${encodeURIComponent(
                                        r.symbol,
                                      )}&strikecount=100`,
                                    )
                                  }
                                  className="fw-semibold lh-sm fs-6 cursor-pointer text-link"
                                >
                                  <u>{r.short}</u>
                                </div>
                              </div>
                            </td>

                            <td>{fmt2(r.volPCR)}</td>

                            <td
                              className={chgClass(
                                toNum(r.optionPCR) === null
                                  ? null
                                  : toNum(r.optionPCR) - 1,
                              )}
                            >
                              {fmt2(r.optionPCR)}
                            </td>

                            <td className={ratioClass(r.volCPR)}>
                              {fmt2(r.volCPR)}
                            </td>

                            <td
                              className={`fw-semibold ${chgClass(r.ltpChange)}`}
                            >
                              {fmt2(r.ltpChange)}
                            </td>

                            <td
                              className={`fw-semibold ${chgClass(r.changePct)}`}
                            >
                              {`${fmt2(r.changePct)}%`}
                            </td>

                            <td className="fw-semibold">{fmt2(r.ltp)}</td>

                            {/* ✅ SHOW ACTUAL BACKEND VALUES (NO manual minus/plus) */}
                            <td
                              className={
                                ce === null
                                  ? "text-muted"
                                  : chgClass(ce) + " fw-semibold"
                              }
                            >
                              {ce === null ? "—" : fmtPct(ce)}
                            </td>

                            <td
                              className={
                                pe === null
                                  ? "text-muted"
                                  : chgClass(pe) + " fw-semibold"
                              }
                            >
                              {pe === null ? "—" : fmtPct(pe)}
                            </td>

                            <td
                              className={
                                showCheckersPct
                                  ? checkerClass(pcrChkPct)
                                  : checkerClass(r.volCheckerPCR)
                              }
                            >
                              {showCheckersPct
                                ? fmtPct(pcrChkPct)
                                : fmt2(r.volCheckerPCR)}
                            </td>

                            <td
                              className={
                                showCheckersPct
                                  ? checkerClass(volChkCprPct)
                                  : checkerClass(r.volCheckerCPR)
                              }
                            >
                              {showCheckersPct
                                ? fmtPct(volChkCprPct)
                                : fmt2(r.volCheckerCPR)}
                            </td>

                            <td className={ratioClass(r.pressureRatio)}>
                              {fmt2(r.pressureRatio)}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default AllCompaniesStock;
